import { withFeatureFlags } from '@/flags.server';
import { dehydrate, type DehydratedState } from '@tanstack/react-query';
import type { InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';

import { getCountryFromRequest } from '@/lib/geo/server';

import { tokenEntityQueryKey } from '@/hooks/tokenQueries/tokenEntityCache';
import { fetchTokenByAddressServer } from '@/hooks/tokenQueries/tokenEntityCache.server';
import { useFeature } from '@/hooks/useFeature';

import { HOMEPAGE_REVAMP } from '@/utils/featureFlags';
import { makeQueryClient } from '@/utils/queryClient';
import { SEARCH_TREND_WALLETS_COOKIE, parseSearchTrendWalletsCookie } from '@/utils/search-trends';
import { resolveDefaultTokenPair } from '@/utils/swap';
import { getTokenInfoFromQueryParameter } from '@/utils/url';

import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { LegacyHomepage } from '../components/HomepageV3/LegacyHomepage';

// The flag defaults to off, so the revamp tree stays out of the legacy visitor's payload.
const Homepage = dynamic(() => import('../components/Homepage').then(({ Homepage }) => Homepage));

type HomePageProps = {
  showStocksAvailabilityBanner: boolean;
  initialWalletConnected: boolean;
  dehydratedState?: DehydratedState;
};

export default function HomePage({
  showStocksAvailabilityBanner,
  initialWalletConnected,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const homepageRevamp = useFeature(HOMEPAGE_REVAMP);

  if (!homepageRevamp) {
    return <LegacyHomepage showStocksAvailabilityBanner={showStocksAvailabilityBanner} />;
  }

  return (
    <div className="overflow-hidden h-fit">
      <Header isHomepage={true} hideSearch={false} />
      <Homepage initialWalletConnected={initialWalletConnected} />
      <div className="h-14" />
      <hr className="border-token-gray-300" />
      <Footer isHomepage={true} />
    </div>
  );
}

export const getServerSideProps = withFeatureFlags<HomePageProps>(async (ctx, featureFlags) => {
  ctx.res.setHeader('Vary', 'X-Vercel-IP-Country');
  // The wallet cookie personalizes the markup, so shared caches must not keep it.
  ctx.res.setHeader('Cache-Control', 'private, no-store');

  const showStocksAvailabilityBanner = getCountryFromRequest(ctx.req) === 'US';
  const initialWalletConnected = !!parseSearchTrendWalletsCookie(
    ctx.req.cookies[SEARCH_TREND_WALLETS_COOKIE],
  );

  if (!featureFlags[HOMEPAGE_REVAMP]) {
    return { props: { showStocksAvailabilityBanner, initialWalletConnected } };
  }

  const buyToken = getTokenInfoFromQueryParameter(ctx.query.buyAddress, ctx.query.buyChain);
  const sellToken = getTokenInfoFromQueryParameter(ctx.query.sellAddress, ctx.query.sellChain);

  const { buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId } =
    resolveDefaultTokenPair({
      buyTokenAddress: buyToken?.address,
      buyTokenChainId: buyToken?.chainId,
      sellTokenAddress: sellToken?.address,
      sellTokenChainId: sellToken?.chainId,
    });

  const queryClient = makeQueryClient();
  const fetchParams = [
    [buyTokenAddress, buyTokenChainId],
    [sellTokenAddress, sellTokenChainId],
  ] as const;

  await Promise.all(
    fetchParams.map(([address, chainId]) => {
      if (!address || !chainId) return;
      return queryClient.prefetchQuery({
        queryKey: tokenEntityQueryKey(address, chainId),
        queryFn: async () => {
          const token = await fetchTokenByAddressServer(address, chainId);
          // Leave unresolved tokens out of hydration so the browser can retry.
          if (!token) throw new Error(`Token not found: ${address}:${chainId}`);
          return token;
        },
        retry: false,
      });
    }),
  );

  return {
    props: {
      showStocksAvailabilityBanner,
      initialWalletConnected,
      dehydratedState: dehydrate(queryClient),
    },
  };
});
